import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../components/Header";
import Card from "../components/Card";
import SimpleTable from "../components/SimpleTable";
import BlueButton from "../components/BlueButton";
import Badge from "../components/Badge";

export default function Support() {
  const [reasons, setReasons] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState("");
  const [remark, setRemark] = useState("");
  const [file, setFile] = useState(null);
  const [uploadedFilePath, setUploadedFilePath] = useState("");

  // Tickets & Pagination
  const [tickets, setTickets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingTickets, setLoadingTickets] = useState(false);

  const authUserString = sessionStorage.getItem("authUserData");
  const authUser = JSON.parse(authUserString);

  const userrole = authUser?.role;
  const userMobile = authUser?.mobile;
  const userEmail = authUser?.email;
  const registrationDate = authUser?.created_at;
  const userId = authUser?.id;
  const userOrgCode = authUser?.org_code;
  const userBusinessName = authUser?.business_name;
  const userState = authUser?.state;
  const userDistrict = authUser?.district;
  const userPincode = authUser?.pincode;
  const idFromSession = authUser?.id;
  const businessNameFromSession = authUser?.business_name;

  const roleMap = { 4: "Distributor" };

  const getISTDateTime = () => {
  const d = new Date();
  const istOffset = 5.5 * 60; // IST offset in minutes
  const utc = d.getTime() + d.getTimezoneOffset() * 60000;
  const istTime = new Date(utc + istOffset * 60000);

  const year = istTime.getFullYear();
  const month = String(istTime.getMonth() + 1).padStart(2, "0");
  const day = String(istTime.getDate()).padStart(2, "0");
  const hours = String(istTime.getHours()).padStart(2, "0");
  const minutes = String(istTime.getMinutes()).padStart(2, "0");
  const seconds = String(istTime.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

  // Format date as YYYY-MM-DD
  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch ticket reasons
  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const res = await axios.get(
          "https://goelectronix.in/api/v1/ticket-reasons"
        );
        if (res.data?.status && Array.isArray(res.data.data)) {
          const filtered = res.data.data.filter((item) => item.role === 4);
          setReasons(filtered);
        }
      } catch (err) {
        console.error("Failed to fetch reasons:", err);
      }
    };
    fetchReasons();
  }, []);

  // Fetch tickets with pagination
  const fetchTickets = async (page = 1) => {
    setLoadingTickets(true);
    try {
      const res = await axios.get(
        `https://goelectronix.in/api/v1/tickets?per_page=10&page=${page}&RetailerCode=${userOrgCode}`
      );

      if (Array.isArray(res.data?.tickets?.data)) {
        const sortedTickets = res.data.tickets.data.sort(
          (a, b) => new Date(b.TicketCreateDate) - new Date(a.TicketCreateDate)
        );
        setTickets(sortedTickets);
        setTotalPages(res.data.tickets.last_page || 1);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error("Failed to fetch tickets:", err);
    } finally {
      setLoadingTickets(false);
    }
  };


  useEffect(() => {
    fetchTickets(1);
  }, []);

  // File upload handler
  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    setFile(selected);
    if (selected) setUploadedFilePath(selected.name);
  };

  // Raise ticket
  const handleRaiseTicket = async () => {
    if (!selectedIssue) {
      Swal.fire("Error", "Please select an issue", "error");
      return;
    }

    const requestBody = {
      CustomerId: 0,
      CustomerName: 0,
      MappedStatus: 0,
      ImieNo1: 0,
      ImieNo2: 0,
      SrNo: 0,
      Brand: 0,
      Model: 0,
      EnterprisesId: 0,
      DeviceId: 0,
      DeviceStatus: 0,
      ApplyPolicy: 0,
      LastSynk: 0,
      LastReport: 0,
      EnrollmentDate: 0,
      TypeOfSoftware: 0,
      KeyType: 0,
      TypeOfTicket: roleMap[userrole],
      PhoneNo: userMobile,
      Email: userEmail,
      RegistrationDate: registrationDate,
      Status: "open",
      RetailerId: userId,
      RetailerCode: userOrgCode,
      RetailerName: userBusinessName,
      State: userState,
      District: userDistrict,
      Pincode: userPincode,
      Issue: selectedIssue,
      Remark: remark,
      TicketCreateDate: getISTDateTime(),
      CreatedByID: idFromSession,
      CreatedByName: businessNameFromSession,
      allocateByName: "",
      allocateByID: "",
      allocateTime: getISTDateTime(),
      role: userrole,
      file_path: uploadedFilePath,
    };

    try {
      const response = await fetch("https://goelectronix.in/api/v1/ticket", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        Swal.fire("Success!", data.message, "success");
        setSelectedIssue("");
        setRemark("");
        setFile(null);
        setUploadedFilePath("");
        fetchTickets(1); // Refresh tickets after submission
      } else {
        const errorData = await response.json();
        Swal.fire(
          "Error!",
          errorData.message || "Failed to create ticket",
          "error"
        );
      }
    } catch (error) {
      console.error("Request error:", error);
      Swal.fire("Oops...", "Something went wrong!", "error");
    }
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      <Header title="Support Tickets" />

      {/* Raise Ticket Form */}
      <Card title="Raise a Ticket">
        <div className="grid grid-cols-2 gap-3">
          <select
            className="rounded-lg border px-3 py-2 text-sm"
            value={selectedIssue}
            onChange={(e) => setSelectedIssue(e.target.value)}
          >
            <option value="">Select Issue</option>
            {reasons.map((reason) => (
              <option key={reason.id} value={reason.reason}>
                {reason.reason}
              </option>
            ))}
          </select>

          <textarea
            placeholder="Describe the issue"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            className="min-h-[20px] max-h-[40px] rounded-lg border px-3 py-2 text-sm"
          />
        </div>
        <div className="flex justify-end mt-2">
          <BlueButton onClick={handleRaiseTicket}>Submit Ticket</BlueButton>
        </div>
      </Card>

      {/* Recent Tickets */}
      <Card title="Recent Tickets">
        {loadingTickets ? (
          <p>Loading tickets...</p>
        ) : (
          <>
            <SimpleTable
              headers={["Ticket ID", "Type", "Subject","Remark", "Status", "Created"]}
              rows={tickets.map((t) => [
                t.id,
                t.TypeOfTicket,
                t.Issue,
                t.Remark,
                <Badge
                key={`b-${t.id}`}
                label={t.Status === "open" ? "Open" : "Close"}
                color={t.Status === "open" ? "blue" : "red"}
              />,
                formatDate(t.TicketCreateDate),
              ])}
            />

            {/* Pagination */}
            <div className="flex justify-between mt-3">
              <button
                disabled={currentPage <= 1}
                onClick={() => fetchTickets(currentPage - 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Previous
              </button>

              <span>
                Page {currentPage} of {totalPages}
              </span>

              <button
                disabled={currentPage >= totalPages}
                onClick={() => fetchTickets(currentPage + 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}


// import { useEffect, useState } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import Header from "../components/Header";
// import Card from "../components/Card";
// import SimpleTable from "../components/SimpleTable";
// import BlueButton from "../components/BlueButton";
// import Badge from "../components/Badge";

// export default function Support() {
//   const [reasons, setReasons] = useState([]);
//   const [selectedIssue, setSelectedIssue] = useState("");
//   const [remark, setRemark] = useState("");
//   const [file, setFile] = useState(null);
//   const [uploadedFilePath, setUploadedFilePath] = useState("");

//   // Tickets & Pagination
//   const [tickets, setTickets] = useState([]);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const [loadingTickets, setLoadingTickets] = useState(false);

//   const authUserString = sessionStorage.getItem("authUserData");
//   const authUser = JSON.parse(authUserString);

//   const userrole = authUser?.role;
//   const userMobile = authUser?.mobile;
//   const userEmail = authUser?.email;
//   const registrationDate = authUser?.created_at;
//   const userId = authUser?.id;
//   const userOrgCode = authUser?.org_code;
//   const userBusinessName = authUser?.business_name;
//   const userState = authUser?.state;
//   const userDistrict = authUser?.district;
//   const userPincode = authUser?.pincode;
//   const idFromSession = authUser?.id;
//   const businessNameFromSession = authUser?.business_name;

//   const roleMap = { 4: "Distributor" };

//   const getISTDateTime = () =>
//     new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });

//   // Fetch ticket reasons
//   useEffect(() => {
//     const fetchReasons = async () => {
//       try {
//         const res = await axios.get(
//           "https://goelectronix.in/api/v1/ticket-reasons"
//         );
//         if (res.data?.status && Array.isArray(res.data.data)) {
//           const filtered = res.data.data.filter((item) => item.role === 4);
//           setReasons(filtered);
//         }
//       } catch (err) {
//         console.error("Failed to fetch reasons:", err);
//       }
//     };
//     fetchReasons();
//   }, []);

//   // Fetch tickets with pagination
//   const fetchTickets = async (page = 1) => {
//     setLoadingTickets(true);
//     try {
//       const res = await axios.get(
//         `https://goelectronix.in/api/v1/tickets?per_page=10&page=${page}&RetailerCode=${userOrgCode}`
//       );
//       if (res.data?.status && Array.isArray(res.data.data)) {
//         const sortedTickets = res.data.data.sort(
//           (a, b) => new Date(b.TicketCreateDate) - new Date(a.TicketCreateDate)
//         );
//         setTickets(sortedTickets);
//         setTotalPages(res.data.last_page || 1);
//         setCurrentPage(page);
//       }
//     } catch (err) {
//       console.error("Failed to fetch tickets:", err);
//     } finally {
//       setLoadingTickets(false);
//     }
//   };

//   useEffect(() => {
//     fetchTickets(1);
//   }, []);

//   // File upload handler
//   const handleFileChange = (e) => {
//     const selected = e.target.files[0];
//     setFile(selected);
//     if (selected) setUploadedFilePath(selected.name);
//   };

//   // Raise ticket
//   const handleRaiseTicket = async () => {
//     if (!selectedIssue) {
//       Swal.fire("Error", "Please select an issue", "error");
//       return;
//     }

//     const requestBody = {
//       CustomerId: 0,
//       CustomerName: 0,
//       MappedStatus: 0,
//       ImieNo1: 0,
//       ImieNo2: 0,
//       SrNo: 0,
//       Brand: 0,
//       Model: 0,
//       EnterprisesId: 0,
//       DeviceId: 0,
//       DeviceStatus: 0,
//       ApplyPolicy: 0,
//       LastSynk: 0,
//       LastReport: 0,
//       EnrollmentDate: 0,
//       TypeOfSoftware: 0,
//       KeyType: 0,
//       TypeOfTicket: roleMap[userrole],
//       PhoneNo: userMobile,
//       Email: userEmail,
//       RegistrationDate: registrationDate,
//       Status: "open",
//       RetailerId: userId,
//       RetailerCode: userOrgCode,
//       RetailerName: userBusinessName,
//       State: userState,
//       District: userDistrict,
//       Pincode: userPincode,
//       Issue: selectedIssue,
//       Remark: remark,
//       TicketCreateDate: getISTDateTime(),
//       CreatedByID: idFromSession,
//       CreatedByName: businessNameFromSession,
//       allocateByName: "",
//       allocateByID: "",
//       allocateTime: getISTDateTime(),
//       role: userrole,
//       file_path: uploadedFilePath,
//     };

//     try {
//       const response = await fetch("https://goelectronix.in/api/v1/ticket", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(requestBody),
//       });

//       if (response.ok) {
//         const data = await response.json();
//         Swal.fire("Success!", data.message, "success");
//         setSelectedIssue("");
//         setRemark("");
//         setFile(null);
//         setUploadedFilePath("");
//         fetchTickets(1); // Refresh tickets after submission
//       } else {
//         const errorData = await response.json();
//         Swal.fire("Error!", errorData.message || "Failed to create ticket", "error");
//       }
//     } catch (error) {
//       console.error("Request error:", error);
//       Swal.fire("Oops...", "Something went wrong!", "error");
//     }
//   };

//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header title="Support Tickets" />

//       {/* Raise Ticket Form */}
//       <Card title="Raise a Ticket">
//         <div className="grid grid-cols-2 gap-3">
//           <select
//             className="rounded-lg border px-3 py-2 text-sm"
//             value={selectedIssue}
//             onChange={(e) => setSelectedIssue(e.target.value)}
//           >
//             <option value="">Select Issue</option>
//             {reasons.map((reason) => (
//               <option key={reason.id} value={reason.reason}>
//                 {reason.reason}
//               </option>
//             ))}
//           </select>

//           <textarea
//             placeholder="Describe the issue"
//             value={remark}
//             onChange={(e) => setRemark(e.target.value)}
//             className="min-h-[20px] max-h-[40px] rounded-lg border px-3 py-2 text-sm"
//           />
//         </div>
//         <div className="flex justify-end mt-2">
//           <BlueButton onClick={handleRaiseTicket}>Submit Ticket</BlueButton>
//         </div>
//       </Card>

//       {/* Recent Tickets */}
//       <Card title="Recent Tickets">
//         {loadingTickets ? (
//           <p>Loading tickets...</p>
//         ) : (
//           <>
//             <SimpleTable
//               headers={["Ticket ID", "Type", "Subject", "Status", "Created"]}
//               rows={tickets.map((t) => [
//                 t.TicketId || t.id,
//                 t.TypeOfTicket,
//                 t.Issue,
//                 Badge(t.Status, t.Status === "open" ? "blue" : "green"),
//                 new Date(t.TicketCreateDate).toLocaleDateString("en-IN"),
//               ])}
//             />

//             {/* Pagination */}
//             <div className="flex justify-between mt-3">
//               <button
//                 disabled={currentPage <= 1}
//                 onClick={() => fetchTickets(currentPage - 1)}
//                 className="px-3 py-1 border rounded disabled:opacity-50"
//               >
//                 Previous
//               </button>

//               <span>
//                 Page {currentPage} of {totalPages}
//               </span>

//               <button
//                 disabled={currentPage >= totalPages}
//                 onClick={() => fetchTickets(currentPage + 1)}
//                 className="px-3 py-1 border rounded disabled:opacity-50"
//               >
//                 Next
//               </button>
//             </div>
//           </>
//         )}
//       </Card>
//     </div>
//   );
// }



// import { useEffect, useState } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import Header from "../components/Header";
// import Card from "../components/Card";
// import SimpleTable from "../components/SimpleTable";
// import BlueButton from "../components/BlueButton";
// import Badge from "../components/Badge";

// export default function Support() {
//   const [reasons, setReasons] = useState([]);
//   const [selectedIssue, setSelectedIssue] = useState("");
//   const [subject, setSubject] = useState("");
//   const [remark, setRemark] = useState("");
//   const [file, setFile] = useState(null);
//   const [uploadedFilePath, setUploadedFilePath] = useState("");

//   const authUserString = sessionStorage.getItem("authUserData");
//   const authUser = JSON.parse(authUserString);

//   // ✅ Dummy session values (replace with real sessionStorage/localStorage values)
//   const userrole = authUser?.role;
//   const userMobile = authUser?.mobile;
//   const userEmail = authUser?.email;
//   const registrationDate = authUser?.created_at;
//   const userId = authUser?.id;
//   const userOrgCode = authUser?.org_code;
//   const userBusinessName = authUser?.business_name;
//   const userState = authUser?.state;
//   const userDistrict = authUser?.district;
//   const userPincode = authUser?.pincode;
//   const idFromSession = authUser?.id;
//   const businessNameFromSession = authUser?.business_name;

//   // ✅ roleMap (adjust if needed)
//   const roleMap = {
//     4: "Distributor",
//   };

//   const getISTDateTime = () => {
//     return new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
//   };

//   // ✅ Fetch reasons (role = 4 only)
//   useEffect(() => {
//     const fetchReasons = async () => {
//       try {
//         const res = await axios.get("https://goelectronix.in/api/v1/ticket-reasons");
//         if (res.data?.status && Array.isArray(res.data.data)) {
//           const filtered = res.data.data.filter((item) => item.role === 4);
//           setReasons(filtered);
//         }
//       } catch (err) {
//         console.error("Failed to fetch reasons:", err);
//       }
//     };
//     fetchReasons();
//   }, []);

//   // ✅ Handle file upload (to your backend if required)
//   const handleFileChange = (e) => {
//     const selected = e.target.files[0];
//     setFile(selected);
//     // for now just keep file name as uploaded path
//     if (selected) setUploadedFilePath(selected.name);
//   };

//   // ✅ Handle form submit
//   const handleRaiseTicket = async () => {
//     if (!selectedIssue) {
//       Swal.fire("Error", "Please select an issue", "error");
//       return;
//     }

//     const requestBody = {
//       CustomerId: "",
//       CustomerName: "",
//       MappedStatus: "",
//       ImieNo1: "",
//       ImieNo2: "",
//       SrNo: "",
//       Brand: "",
//       Model: "",
//       EnterprisesId: "",
//       DeviceId: "",
//       DeviceStatus: "",
//       ApplyPolicy: "",
//       LastSynk: "",
//       LastReport: "",
//       EnrollmentDate: "",
//       TypeOfSoftware: "",
//       KeyType: "",
//       // ---------------------------------------
//       TypeOfTicket: roleMap[userrole],
//       PhoneNo: userMobile,
//       Email: userEmail,
//       RegistrationDate: registrationDate,
//       Status: "open",
//       RetailerId: userId,
//       RetailerCode: userOrgCode,
//       RetailerName: userBusinessName,
//       State: userState,
//       District: userDistrict,
//       Pincode: userPincode,
//       Issue: selectedIssue,
//       Remark: remark,
//       TicketCreateDate: getISTDateTime(),
//       CreatedByID: idFromSession,
//       CreatedByName: businessNameFromSession,
//       allocateByName: "",
//       allocateByID: "",
//       allocateTime: getISTDateTime(),
//       role: userrole,
//       file_path: uploadedFilePath,
//     };

//     try {
//       const response = await fetch("https://goelectronix.in/api/v1/ticket", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(requestBody),
//       });

//       if (response.ok) {
//         const data = await response.json();
//         Swal.fire("Success!", data.message, "success");
//         // ✅ Reset form
//         setSelectedIssue("");
//         setSubject("");
//         setRemark("");
//         setFile(null);
//         setUploadedFilePath("");
//       } else {
//         const errorData = await response.json();
//         Swal.fire("Error!", errorData.message || "Failed to create ticket", "error");
//       }
//     } catch (error) {
//       console.error("Request error:", error);
//       Swal.fire("Oops...", "Something went wrong!", "error");
//     }
//   };

//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header title="Support Tickets" />

//       {/* Raise Ticket Form */}
//       <Card title="Raise a Ticket">
//         <div className="grid grid-cols-2 gap-3">
//           {/* ✅ Dropdown from API */}
//           <select
//             className="rounded-lg border px-3 py-2 text-sm"
//             value={selectedIssue}
//             onChange={(e) => setSelectedIssue(e.target.value)}
//           >
//             <option value="">Select Issue</option>
//             {reasons.map((reason) => (
//               <option key={reason.id} value={reason.reason}>
//                 {reason.reason}
//               </option>
//             ))}
//           </select>

//           {/* ✅ Describe the issue */}
//           <textarea
//             placeholder="Describe the issue"
//             value={remark}
//             onChange={(e) => setRemark(e.target.value)}
//             className="min-h-[20px] max-h-[40px] rounded-lg border px-3 py-2 text-sm"
//           />

          
          
//         </div>
//         {/* ✅ Submit Button */}
//         <div className="flex justify-end">
//           <BlueButton onClick={handleRaiseTicket}>Submit Ticket</BlueButton>
//         </div>
//       </Card>


//       {/* Recent Tickets (static for now) */}
//       <Card title="Recent Tickets">
//         <SimpleTable
//           headers={["Ticket ID", "Type", "Subject", "Status", "Created"]}
//           rows={[
//             [
//               "TCK-1021",
//               "Payment",
//               "Missing credit for INV-90021",
//               Badge("Open", "blue"),
//               "2025-09-22",
//             ],
//             [
//               "TCK-1017",
//               "Key",
//               "Key range not working",
//               Badge("Resolved"),
//               "2025-09-20",
//             ],
//           ]}
//         />
//       </Card>
//     </div>
//   );
// }


// import { useEffect, useState } from "react";
// import axios from "axios";
// import Header from "../components/Header";
// import Card from "../components/Card";
// import SimpleTable from "../components/SimpleTable";
// import BlueButton from "../components/BlueButton";
// import Badge from "../components/Badge";

// export default function Support() {
//   const [reasons, setReasons] = useState([]);

//   useEffect(() => {
//     const fetchReasons = async () => {
//       try {
//         const res = await axios.get("https://goelectronix.in/api/v1/ticket-reasons");
//         if (res.data?.status && Array.isArray(res.data.data)) {
//           // ✅ filter only role = 4
//           const filtered = res.data.data.filter((item) => item.role === 4);
//           setReasons(filtered);
//         }
//       } catch (err) {
//         console.error("Failed to fetch reasons:", err);
//       }
//     };

//     fetchReasons();
//   }, []);

//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header title="Support Tickets" />

//       {/* Raise Ticket Form */}
//       <Card title="Raise a Ticket">
//         <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
//           {/* ✅ Dropdown populated from API */}
//           <select className="rounded-lg border px-3 py-2 text-sm">
//             <option value="">Select Issue</option>
//             {reasons.map((reason) => (
//               <option key={reason.id} value={reason.reason}>
//                 {reason.reason}
//               </option>
//             ))}
//           </select>

//           <input
//             placeholder="Subject"
//             className="rounded-lg border px-3 py-2 text-sm"
//           />

//           <textarea
//             placeholder="Describe the issue"
//             className="sm:col-span-2 min-h-[120px] rounded-lg border px-3 py-2 text-sm"
//           />

//           <div className="sm:col-span-2 flex items-center justify-between">
//             <input type="file" className="text-sm" />
//             <BlueButton>Submit Ticket</BlueButton>
//           </div>
//         </div>
//       </Card>

//       {/* Recent Tickets */}
//       <Card title="Recent Tickets">
//         <SimpleTable
//           headers={["Ticket ID", "Type", "Subject", "Status", "Created"]}
//           rows={[
//             [
//               "TCK-1021",
//               "Payment",
//               "Missing credit for INV-90021",
//               Badge("Open", "blue"),
//               "2025-09-22",
//             ],
//             [
//               "TCK-1017",
//               "Key",
//               "Key range not working",
//               Badge("Resolved"),
//               "2025-09-20",
//             ],
//           ]}
//         />
//       </Card>
//     </div>
//   );
// }


// import Header from "../components/Header";
// import Card from "../components/Card";
// import SimpleTable from "../components/SimpleTable";
// import BlueButton from "../components/BlueButton";
// import Badge from "../components/Badge";
// export default function Support() {
//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header title="Support Tickets" />
//       <Card title="Raise a Ticket">
//         <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
//           <select className="rounded-lg border px-3 py-2 text-sm">
//             <option>Key Issue</option>
//             <option>Payment Issue</option>
//             <option>Inventory Issue</option>
//             <option>Other</option>
//           </select>
//           <input placeholder="Subject" className="rounded-lg border px-3 py-2 text-sm" />
//           <textarea placeholder="Describe the issue" className="sm:col-span-2 min-h-[120px] rounded-lg border px-3 py-2 text-sm" />
//           <div className="sm:col-span-2 flex items-center justify-between">
//             <input type="file" className="text-sm" />
//             <BlueButton>Submit Ticket</BlueButton>
//           </div>
//         </div>
//       </Card>
//       <Card title="Recent Tickets">
//         <SimpleTable
//           headers={["Ticket ID", "Type", "Subject", "Status", "Created"]}
//           rows={[["TCK-1021", "Payment", "Missing credit for INV-90021", Badge("Open", "blue"), "2025-09-22"], ["TCK-1017", "Key", "Key range not working", Badge("Resolved"), "2025-09-20"]]}
//         />
//       </Card>
//     </div>
//   );
// }